import moment from 'moment-timezone';
import {
    DateIso,
    DateTimeIso,
    DayOfWeek,
    DisplayDate,
    EditDate,
    Nullable,
    NullableDate,
    Time24Hr,
    TimeIso
} from './Types';

export type FactorOfSixty = 1 | 2 | 3 | 4 | 5 | 6 | 10 | 12 | 15 | 20 | 30 | 60;

export enum ValidDateFormats {
    Day = 'D',
    ShortDisplay = 'DD MMM YY',
    Display = 'DD MMM YYYY',
    DisplayWithHyphen = 'DD-MMM-YYYY',
    DisplayWithHyphenShortMonth = 'DD-MM-YYYY',
    Edit = 'DD/MM/YYYY',
    DisplaySlashSpace = 'DD / MM / YYYY',
    ExpiryEdit = 'MM/YYYY',
    Year = 'YYYY',
    IsoFormat = 'YYYY-MM-DD',
    DateTimeIso = 'YYYY-MM-DD HH:mm:ss',
    NodeDateTimeIso = 'YYYY-MM-DDTHH:mm:ss',
    NodeDateTimeTimezone = 'YYYY-MM-DDTHH:mm:ssZ',
    PrettyPrintFullDateTime = 'DD MMM YYYY hh:mm A',
    PrettyPrintFullLongDateTime = 'DD MMM YYYY hh:mm:ss A',
    PrettyPrintTimeThenFullDate = 'h:mm a, D MMMM YYYY',
    PrettyPrintFullDateTimeWithBullet = 'DD MMM YYYY • hh:mm A'
}

export enum ValidTimeFormats {
    Format24HR = 'HH:mm',
    IsoFormat = 'HH:mm:ss',
    AMPMForm = 'hh:mm A'
}

export const daysOfWeek: DayOfWeek[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

export function sortDateTime(dateTime: Date, dateTime2: Date, asc = true): number {
    if (asc) return dateTime.getTime() - dateTime2.getTime();
    else return dateTime2.getTime() - dateTime.getTime();
}

export function splitDateTimeToStrings(dateTime: Nullable<string> | false): { date: string; time: string } {
    if (!dateTime) return { date: '', time: '' };

    const targetMoment = moment(dateTime, ValidDateFormats.NodeDateTimeIso, moment.tz.guess());

    const date: string = targetMoment.format(ValidDateFormats.Edit);
    const time: string = targetMoment.format(ValidTimeFormats.Format24HR);

    return { date, time };
}

export function convertStringsToMoment(date: string, time: string, timeZone?: string): moment.Moment {
    if (!!timeZone) return moment.tz(`${date} ${time}`, 'DD/MM/YYYY HH:mm', timeZone);
    else return moment.tz(`${date} ${time}`, 'DD/MM/YYYY HH:mm', moment.tz.guess());
}

export function getNextDay(day: DayOfWeek): DayOfWeek {
    const dayIndex = daysOfWeek.findIndex(it => day === it);
    let nextDayIndex = dayIndex + 1;
    if (nextDayIndex > daysOfWeek.length - 1) nextDayIndex = 0;

    return daysOfWeek[nextDayIndex];
}

export function getPreviousDay(day: DayOfWeek): DayOfWeek {
    const dayIndex = daysOfWeek.findIndex(it => day === it);
    const previousDayIndex = dayIndex === 0 ? daysOfWeek.length - 1 : dayIndex - 1;

    return daysOfWeek[previousDayIndex];
}

moment.updateLocale('en', {
    longDateFormat: {
        LT: 'HH:mm',
        LTS: 'HH:mm:ss',
        L: 'DD/MM/YYYY',
        l: 'D/M/YYYY',
        LL: 'Do MMMM YYYY',
        ll: 'DD MMM YYYY',
        LLL: 'Do MMMM YYYY LT',
        lll: 'D MMM YYYY LT',
        LLLL: 'dddd, Do MMMM YYYY LT',
        llll: 'ddd, D MMM YYYY LT'
    },
    week: {
        doy: 6,
        dow: 1 // Monday is the first day of the week
    }
});

export function roundTimeIsoToEarlierMinutes(time: TimeIso, interval: FactorOfSixty): TimeIso {
    return roundTimeIsoToMinutes(time, interval, false);
}

export function roundTimeIsoToLaterMinutes(time: TimeIso, interval: FactorOfSixty): TimeIso {
    return roundTimeIsoToMinutes(time, interval, true);
}

function roundTimeIsoToMinutes(time: TimeIso, interval: FactorOfSixty, roundUp: boolean): TimeIso {
    const currentMoment = moment(time, ValidTimeFormats.Format24HR);
    if (time && currentMoment.isValid) {
        const minutes = currentMoment.minutes();

        let m: number;
        if (roundUp) m = Math.ceil(minutes / interval) * interval;
        else m = Math.floor(minutes / interval) * interval;

        return currentMoment.minutes(m).format(ValidTimeFormats.Format24HR);
    }

    return time;
}

export function convertEditDateToIsoDate(date: EditDate): DateIso {
    if (!date) return '';

    const parsedDate = parseDateString(date);

    if (parsedDate) return moment(parsedDate).format(ValidDateFormats.IsoFormat);

    return '';
}

export function convertDateTimeToMoment(date: DateTimeIso): moment.Moment {
    return moment(date);
}

export function convertDateTimeToDateIso(date: Date): string {
    return moment(date).format(ValidDateFormats.IsoFormat);
}

export function convertTimeIsoToTime24Hr(timeIso: TimeIso): Time24Hr {
    const [hour, minute] = timeIso.split(':');
    if (hour && minute) return `${hour}:${minute}`;

    return '';
}

export function convertTime24HrToTimeIso(time: Time24Hr): TimeIso {
    if (!time) return time;

    return time + ':00';
}

export function convertDateIsoToDisplayDate(date: DateIso): DisplayDate {
    if (!date) return '';

    const parsedDate = moment(date, ValidDateFormats.IsoFormat);

    if (parsedDate.isValid()) return parsedDate.format(ValidDateFormats.Display);

    return '';
}

export function convertDateIsoToOther(date: DateIso, targetFormat: ValidDateFormats): string {
    const dateAsMoment = moment(date, ValidDateFormats.IsoFormat);
    return dateAsMoment.isValid() ? dateAsMoment.format(targetFormat) : '';
}

export function parseDateString(date: DisplayDate | EditDate | DateIso): NullableDate {
    if (!date) return null;

    const parsedDate = moment(date, [ValidDateFormats.Edit, ValidDateFormats.Display, ValidDateFormats.IsoFormat]);
    if (parsedDate.isValid()) return parsedDate.toDate();

    return null;
}

export function ageFromDateIso(birthDate: DateIso, isDeceased?: boolean, deathDate?: DateIso): string {
    if (!birthDate) return '';

    if (isDeceased && !deathDate) return '';

    const startDate = moment(birthDate);
    const endDate = deathDate ? moment(deathDate) : moment();

    startDate.startOf('day');
    endDate.startOf('day');

    const diffInDays = endDate.diff(startDate, 'days');
    const diffInMonths = endDate.diff(startDate, 'months');
    const diffInYears = endDate.diff(startDate, 'years', true);

    if (diffInDays <= 21) {
        return `${endDate.diff(startDate, 'days')}d`;
    } else if (diffInMonths <= 2) {
        return `${endDate.diff(startDate, 'weeks')}w`;
    } else if (diffInMonths <= 12) {
        return `${endDate.diff(startDate, 'months')}m`;
    } else if (diffInYears <= 10) {
        const ageInYears = endDate.diff(startDate, 'y');

        let returnValue = `${ageInYears}y`;
        const remainingMonths = endDate.diff(moment(birthDate).add(ageInYears, 'years'), 'months');
        if (remainingMonths > 0) {
            returnValue += ` ${remainingMonths}m`;
        }

        return returnValue;
    }

    return `${endDate.diff(startDate, 'years')}y`;
}

export function convertDateOrNullToString(date: Nullable<Date>, format = 'DD/MM/YYYY') {
    return !!date ? moment(date).format(format) : null;
}

export function duration(firstDate: moment.Moment, secondDate: moment.Moment): string {
    let fromDate = firstDate;
    let toDate = secondDate;

    if (!fromDate.isValid() || !toDate.isValid()) return '';

    fromDate.endOf('day');
    toDate.endOf('day');

    if (fromDate > toDate) {
        const tempDate = toDate;
        toDate = fromDate;
        fromDate = tempDate;
    }

    // Moment#diff is not inclusive of the start 'day' so diff from the previous day
    fromDate = fromDate.add(-1, 'days');

    const yearsDiff = toDate.diff(fromDate, 'years');
    const yearsOutput = `${yearsDiff} ${yearsDiff > 1 ? 'years' : 'year'}`;
    if (yearsDiff >= 3) return `${yearsDiff} years`;
    fromDate = fromDate.add(yearsDiff, 'years');

    const monthsDiff = toDate.diff(fromDate, 'months');
    const monthsOutput = `${monthsDiff} ${monthsDiff > 1 ? 'months' : 'month'}`;
    fromDate = fromDate.add(monthsDiff, 'months');

    if (yearsDiff > 0) return monthsDiff > 0 ? `${yearsOutput} ${monthsOutput}` : yearsOutput;

    const weeksDiff = toDate.diff(fromDate, 'weeks');
    const weeksOutput = `${weeksDiff} ${weeksDiff > 1 ? 'weeks' : 'week'}`;
    fromDate = fromDate.add(weeksDiff, 'weeks');

    if (monthsDiff > 0) return weeksDiff > 0 ? `${monthsOutput} ${weeksOutput}` : monthsOutput;

    const daysDiff = toDate.diff(fromDate, 'days');
    const daysOutput = `${daysDiff} ${daysDiff > 1 ? 'days' : 'day'}`;

    if (weeksDiff > 0) return daysDiff > 0 ? `${weeksOutput} ${daysOutput}` : weeksOutput;
    if (daysDiff > 0) return daysOutput;

    return '';
}
